import { useQuery } from '@tanstack/react-query'
import { X, Shield, Globe, Zap, Activity } from 'lucide-react'
import { apiClient } from '@/lib/api'
import type { CountryIntelData } from '@/lib/api'

interface CountryIntelPanelProps {
  countryCode: string | null
  onClose: () => void
}

const DOMAIN_COLORS: Record<string, { bg: string; bar: string; text: string }> = {
  cyber: { bg: 'bg-purple-500/20', bar: 'bg-purple-500', text: 'text-purple-400' },
  geopolitical: { bg: 'bg-red-500/20', bar: 'bg-red-500', text: 'text-red-400' },
  infrastructure: { bg: 'bg-amber-500/20', bar: 'bg-amber-500', text: 'text-amber-400' },
  conflict: { bg: 'bg-rose-500/20', bar: 'bg-rose-500', text: 'text-rose-400' },
  economic: { bg: 'bg-blue-500/20', bar: 'bg-blue-500', text: 'text-blue-400' },
}

const SEVERITY_DOT: Record<string, string> = {
  critical: 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.5)]',
  high: 'bg-orange-500 shadow-[0_0_6px_rgba(249,115,22,0.5)]',
  medium: 'bg-yellow-500 shadow-[0_0_6px_rgba(234,179,8,0.5)]',
  low: 'bg-blue-500 shadow-[0_0_6px_rgba(59,130,246,0.5)]',
  info: 'bg-gray-500',
}

const SIGNAL_STYLE: Record<string, string> = {
  critical: 'border-red-500/40 bg-red-500/10 text-red-400',
  high: 'border-orange-500/40 bg-orange-500/10 text-orange-400',
  elevated: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400',
  normal: 'border-green-900/40 bg-green-500/10 text-green-400',
}

function countryCodeToFlag(code: string): string {
  return [...code.toUpperCase()].map(c =>
    String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)
  ).join('')
}

function getScoreColor(score: number): { ring: string; text: string } {
  if (score >= 70) return { ring: '#ef4444', text: 'text-red-400' }
  if (score >= 45) return { ring: '#f97316', text: 'text-orange-400' }
  if (score >= 20) return { ring: '#eab308', text: 'text-yellow-400' }
  return { ring: '#22c55e', text: 'text-green-400' }
}

function timeAgo(ts: string): string {
  const seconds = Math.floor((Date.now() - new Date(ts).getTime()) / 1000)
  if (seconds < 60) return 'now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

export default function CountryIntelPanel({ countryCode, onClose }: CountryIntelPanelProps) {
  const open = countryCode !== null

  const { data, isLoading } = useQuery<CountryIntelData>({
    queryKey: ['country-intel', countryCode],
    queryFn: () => apiClient.getCountryIntel(countryCode!),
    enabled: open,
    staleTime: 60_000,
    retry: 2,
  })

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-40 transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-[400px] z-50 transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full bg-gray-950/95 backdrop-blur-md border-l border-green-900/50 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-green-900/40 bg-gray-900/60">
            <div className="flex items-center gap-2 min-w-0">
              {countryCode && (
                <span className="text-xl leading-none">{countryCodeToFlag(countryCode)}</span>
              )}
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-100 truncate">
                  {isLoading ? countryCode : data?.country_name ?? countryCode}
                </h2>
                <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
                  COUNTRY INTEL
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-300 transition-colors p-1 rounded hover:bg-gray-800/50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
            {isLoading ? <LoadingSkeleton /> : data ? <IntelContent data={data} /> : null}
          </div>
        </div>
      </div>
    </>
  )
}

function IntelContent({ data }: { data: CountryIntelData }) {
  const scoreColor = getScoreColor(data.instability_score)
  const pct = Math.min(data.instability_score, 100)
  const circumference = 2 * Math.PI * 34
  const offset = circumference - (pct / 100) * circumference
  const maxDomainCount = Math.max(...Object.values(data.events_by_domain), 1)

  return (
    <div className="p-4 space-y-4">
      {/* Instability Score */}
      <section className="bg-gray-900/80 border border-green-900/40 rounded p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Activity className={`w-3 h-3 ${scoreColor.text}`} />
          <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
            Instability Score
          </span>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative w-16 h-16 shrink-0">
            <svg viewBox="0 0 76 76" className="w-full h-full -rotate-90">
              <circle cx="38" cy="38" r="34" fill="none" stroke="#1e293b" strokeWidth="4" />
              <circle
                cx="38" cy="38" r="34" fill="none"
                stroke={scoreColor.ring}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                className="transition-all duration-1000"
                style={{ filter: `drop-shadow(0 0 6px ${scoreColor.ring}60)` }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-lg font-bold font-mono ${scoreColor.text}`}>
                {Math.round(pct)}
              </span>
            </div>
          </div>
          <div className="flex-1 min-w-0 space-y-1">
            <div className="text-[10px] text-gray-500 font-mono">
              Total Events: <span className="text-gray-300">{data.total_events}</span>
            </div>
            {data.anomalies.length > 0 && (
              <div className="text-[9px] text-amber-400 font-mono">
                {data.anomalies.length} anomal{data.anomalies.length === 1 ? 'y' : 'ies'} detected
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Domain Breakdown */}
      {Object.keys(data.events_by_domain).length > 0 && (
        <section className="bg-gray-900/80 border border-green-900/40 rounded p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Globe className="w-3 h-3 text-green-400" />
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
              Domain Breakdown
            </span>
          </div>
          <div className="space-y-1.5">
            {Object.entries(data.events_by_domain)
              .sort(([, a], [, b]) => b - a)
              .map(([domain, count]) => {
                const colors = DOMAIN_COLORS[domain] || DOMAIN_COLORS.cyber
                const widthPct = (count / maxDomainCount) * 100
                return (
                  <div key={domain} className="space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-mono capitalize ${colors.text}`}>{domain}</span>
                      <span className="text-[10px] font-mono text-gray-400">{count}</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${colors.bar} transition-all duration-700`}
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                )
              })}
          </div>
        </section>
      )}

      {/* Severity Breakdown */}
      {Object.keys(data.events_by_severity).length > 0 && (
        <section className="bg-gray-900/80 border border-green-900/40 rounded p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Shield className="w-3 h-3 text-green-400" />
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
              Severity Breakdown
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(data.events_by_severity)
              .sort(([, a], [, b]) => b - a)
              .map(([sev, count]) => (
                <div key={sev} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${SEVERITY_DOT[sev] || 'bg-gray-500'}`} />
                  <span className="text-[10px] font-mono text-gray-400 capitalize">{sev}</span>
                  <span className="text-[10px] font-mono text-gray-500">{count}</span>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Active Actors */}
      {data.active_actors.length > 0 && (
        <section className="bg-gray-900/80 border border-green-900/40 rounded p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Shield className="w-3 h-3 text-purple-400" />
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
              Active Actors
            </span>
            <span className="text-[9px] text-gray-700 font-mono">({data.active_actors.length})</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.active_actors.map((actor) => (
              <span
                key={actor}
                className="bg-purple-500/15 text-purple-400 border border-purple-500/30 px-2 py-0.5 rounded text-[9px] font-mono"
              >
                {actor}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Correlation Signals */}
      {data.correlation_signals.length > 0 && (
        <section className="bg-gray-900/80 border border-green-900/40 rounded p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Zap className="w-3 h-3 text-amber-400" />
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
              Correlation Signals
            </span>
          </div>
          <div className="space-y-1.5">
            {data.correlation_signals.map((sig, i) => {
              const style = SIGNAL_STYLE[sig.signal_level] || SIGNAL_STYLE.normal
              return (
                <div
                  key={i}
                  className={`border rounded px-2.5 py-1.5 ${style}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] font-mono uppercase tracking-wider font-bold">
                      {sig.signal_level}
                    </span>
                    <span className="text-[9px] font-mono opacity-70">
                      {sig.event_count} events
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {sig.domains_active.map((d) => (
                      <span
                        key={d}
                        className="text-[8px] font-mono bg-black/20 rounded px-1.5 py-0.5 capitalize"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Anomalies */}
      {data.anomalies.length > 0 && (
        <section className="bg-gray-900/80 border border-red-900/30 rounded p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Activity className="w-3 h-3 text-red-400" />
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
              Anomalies
            </span>
          </div>
          <div className="space-y-1">
            {data.anomalies.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-[10px] font-mono py-0.5">
                <span className="text-gray-400 truncate max-w-[200px]">{a.entity}</span>
                <div className="flex items-center gap-2">
                  <span className={a.change_pct > 0 ? 'text-red-400' : 'text-green-400'}>
                    {a.change_pct > 0 ? '+' : ''}{a.change_pct.toFixed(0)}%
                  </span>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    a.alert_level === 'critical' ? 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.5)]'
                    : a.alert_level === 'high' ? 'bg-orange-500 shadow-[0_0_6px_rgba(249,115,22,0.5)]'
                    : 'bg-yellow-500 shadow-[0_0_6px_rgba(234,179,8,0.5)]'
                  }`} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent Events */}
      {data.recent_events.length > 0 && (
        <section className="bg-gray-900/80 border border-green-900/40 rounded p-3">
          <div className="flex items-center gap-1.5 mb-2.5">
            <Zap className="w-3 h-3 text-green-400" />
            <span className="text-[9px] text-gray-600 uppercase tracking-[0.15em] font-mono">
              Recent Events
            </span>
            <span className="text-[9px] text-gray-700 font-mono">({data.recent_events.length})</span>
          </div>
          <div className="space-y-0.5 max-h-[240px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
            {data.recent_events.map((ev, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 py-1.5 px-1 rounded hover:bg-green-500/10 transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${SEVERITY_DOT[ev.severity] || 'bg-gray-500'}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] text-gray-300 font-sans leading-snug truncate">
                    {ev.title}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5 text-[9px] font-mono text-gray-600">
                    <span className="capitalize">{ev.event_type.replace(/_/g, ' ')}</span>
                    <span className="text-gray-800">·</span>
                    <span>{ev.source}</span>
                    <span className="text-gray-800">·</span>
                    <span>{timeAgo(ev.timestamp)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="p-4 space-y-4">
      {/* Score skeleton */}
      <div className="bg-gray-900/80 border border-green-900/40 rounded p-3">
        <div className="h-3 w-24 bg-gray-800/50 rounded animate-pulse mb-3" />
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full border-2 border-gray-800/50 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 bg-gray-800/50 rounded animate-pulse" />
            <div className="h-2 w-20 bg-gray-800/50 rounded animate-pulse" />
          </div>
        </div>
      </div>
      {/* Bar skeleton */}
      <div className="bg-gray-900/80 border border-green-900/40 rounded p-3">
        <div className="h-3 w-28 bg-gray-800/50 rounded animate-pulse mb-3" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="mb-2 space-y-1">
            <div className="flex justify-between">
              <div className="h-2.5 w-16 bg-gray-800/50 rounded animate-pulse" />
              <div className="h-2.5 w-6 bg-gray-800/50 rounded animate-pulse" />
            </div>
            <div className="h-1.5 bg-gray-800/50 rounded-full animate-pulse" />
          </div>
        ))}
      </div>
      {/* Severity skeleton */}
      <div className="bg-gray-900/80 border border-green-900/40 rounded p-3">
        <div className="h-3 w-32 bg-gray-800/50 rounded animate-pulse mb-3" />
        <div className="flex gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-3 w-14 bg-gray-800/50 rounded animate-pulse" />
          ))}
        </div>
      </div>
      {/* Events skeleton */}
      <div className="bg-gray-900/80 border border-green-900/40 rounded p-3">
        <div className="h-3 w-24 bg-gray-800/50 rounded animate-pulse mb-3" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-start gap-2 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-gray-800/50 mt-1.5" />
            <div className="flex-1 space-y-1">
              <div className="h-3 bg-gray-800/50 rounded animate-pulse" />
              <div className="h-2 w-3/4 bg-gray-800/50 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
